import { useState } from "react";
import { Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  ALL_VALUE,
  APPOINTMENT_STATUSES,
  APPOINTMENT_STATUS_LABELS,
} from "@/features/appointment/constants";
import { useBrokers } from "@/features/appointment/hooks/use-brokers";
import { useAuthStore } from "@/lib/auth-store";
import { seesAllAppointments } from "@/features/settings/role-labels";
import type { AppointmentStatus } from "@/features/appointment/types";

interface AppointmentFiltersProps {
  brokerId: string;
  status: string;
  onBrokerChange: (value: string) => void;
  onStatusChange: (value: string) => void;
}

export function AppointmentFilters(props: AppointmentFiltersProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  // Agents see only viewings they're a party to (backend-enforced), so the
  // broker filter is meaningless for them — show it only to OWNER/MANAGER.
  const canFilterByBroker = seesAllAppointments(useAuthStore((s) => s.user)?.role);
  return (
    <>
      {/* Desktop inline */}
      <div className="hidden flex-wrap items-end gap-3 md:flex">
        {canFilterByBroker ? (
          <BrokerSelect
            value={props.brokerId}
            onChange={props.onBrokerChange}
          />
        ) : null}
        <StatusSelect
          value={props.status}
          onChange={props.onStatusChange}
        />
      </div>

      {/* Mobile sheet */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            aria-label="Bộ lọc"
            className="md:hidden"
          >
            <Filter aria-hidden="true" />
            Bộ lọc
          </Button>
        </SheetTrigger>
        <SheetContent side="bottom" className="max-h-[60vh]">
          <SheetHeader>
            <SheetTitle>Bộ lọc lịch hẹn</SheetTitle>
            <SheetDescription>
              Lọc theo đại diện hoặc trạng thái lịch hẹn.
            </SheetDescription>
          </SheetHeader>
          <div className="flex flex-col gap-4 px-4 pb-6">
            {canFilterByBroker ? (
              <BrokerSelect
                value={props.brokerId}
                onChange={props.onBrokerChange}
              />
            ) : null}
            <StatusSelect
              value={props.status}
              onChange={props.onStatusChange}
            />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

function BrokerSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const brokers = useBrokers();
  return (
    <div className="space-y-1.5">
      <Label htmlFor="appointmentBroker" className="text-xs">
        Đại diện
      </Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id="appointmentBroker" className="w-48">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_VALUE}>Tất cả</SelectItem>
          {(brokers.data ?? []).map((b) => (
            <SelectItem key={b.id} value={String(b.id)}>
              {b.fullName}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function StatusSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor="appointmentStatus" className="text-xs">
        Trạng thái
      </Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id="appointmentStatus" className="w-48">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_VALUE}>Tất cả</SelectItem>
          {APPOINTMENT_STATUSES.map((status: AppointmentStatus) => (
            <SelectItem key={status} value={status}>
              {APPOINTMENT_STATUS_LABELS[status]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
